import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Mountain, CheckCircle, Clock, Phone, MessageCircle, Calendar, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BookingConfirmation = () => {
  const [searchParams] = useSearchParams();
  const bookingId = searchParams.get('id');
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (bookingId) {
      fetchBookingStatus();
    }
  }, [bookingId]);

  const fetchBookingStatus = async () => {
    try {
      const response = await fetch(`${API_URL}/api/bookings/${bookingId}/status`);
      if (response.ok) {
        const data = await response.json();
        setBooking(data);
      }
    } catch (error) {
      console.error('Error fetching booking:', error);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#2C5F8D]"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <Mountain className="h-8 w-8 text-[#2C5F8D]" />
              <span className="text-xl font-bold text-[#2C5F8D]" style={{fontFamily: 'Playfair Display, serif'}}>
                GoHimalayan
              </span>
            </Link>
          </div>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          {/* Success Icon */}
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-3">
            Booking Request Submitted!
          </h1>
          
          <p className="text-lg text-gray-600 mb-8">
            Your request has been sent to the host. They will respond within 24 hours.
          </p>

          {/* Booking Details */}
          {booking && (
            <div className="bg-gray-50 rounded-xl p-6 mb-8 text-left">
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-500">Booking ID</span>
                <span className="font-mono text-sm">{booking.booking_id}</span>
              </div>
              <div className="flex items-center justify-between mb-4">
                <span className="text-sm text-gray-500">Status</span>
                <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium">
                  <Clock className="w-4 h-4 inline mr-1" />
                  {booking.status === 'pending' ? 'Awaiting Host Response' : booking.status}
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-500">Total Amount</span>
                <span className="text-xl font-bold text-gray-900">₹{booking.total_amount?.toLocaleString()}</span>
              </div>
            </div>
          )}

          {/* What's Next */}
          <div className="text-left mb-8">
            <h2 className="font-semibold text-gray-900 mb-4">What happens next?</h2>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-blue-600">1</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Host reviews your request</p>
                  <p className="text-sm text-gray-500">The host will check availability and respond within 24 hours</p>
                </div>
              </div>
              
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-blue-600">2</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Complete payment</p>
                  <p className="text-sm text-gray-500">Once approved, you'll receive a link to complete payment</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-sm font-bold text-blue-600">3</span>
                </div>
                <div>
                  <p className="font-medium text-gray-900">Get confirmation</p>
                  <p className="text-sm text-gray-500">Receive your booking confirmation with check-in details</p>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-blue-50 rounded-xl p-4 mb-8">
            <p className="text-sm text-blue-800">
              <MessageCircle className="w-4 h-4 inline mr-1" />
              We'll send you updates on WhatsApp. Make sure your number is correct.
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/stays" className="flex-1">
              <Button variant="outline" className="w-full">
                Browse More Stays
              </Button>
            </Link>
            <Link to="/" className="flex-1">
              <Button className="w-full bg-[#2C5F8D] hover:bg-[#1e4570]">
                Back to Home
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmation;
